import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import type { EntryViewT } from "@/types/database/entries";
import dayjs from "dayjs";
import { Area, AreaChart, XAxis, YAxis } from "recharts";
import {
  convertEntriesToChartConfig,
  convertEntriesToChartData,
} from "./utils";

// Map compliance values to numeric scores and colors for better visualization
const complianceScoreMap = {
  "Not at all": {
    score: 0,
    color: "hsl(var(--destructive))",
    bgColor: "bg-red-200",
    textColor: "text-red-700",
    label: "NOT FOLLOWING advice",
  },
  Sometimes: {
    score: 1,
    color: "hsl(var(--warning))",
    bgColor: "bg-orange-200",
    textColor: "text-orange-700",
    label: "SOMETIMES following advice",
  },
  Mostly: {
    score: 2,
    color: "hsl(var(--success))",
    bgColor: "bg-yellow-200",
    textColor: "text-yellow-700",
    label: "MOSTLY following advice",
  },
  Always: {
    score: 3,
    color: "hsl(var(--success))",
    bgColor: "bg-green-200",
    textColor: "text-green-700",
    label: "ALWAYS following advice",
  },
} as const;

type ComplianceValue = keyof typeof complianceScoreMap;

export function HeelPainComplianceChart({
  entries,
  disableAnimations = false,
  formId,
}: {
  entries: EntryViewT[];
  disableAnimations?: boolean;
  formId: string;
}) {
  const chartConfig = convertEntriesToChartConfig(entries, formId);
  const chartData = convertEntriesToChartData(entries, formId);
  console.log(entries);

  console.log(chartData);
  // Transform the data to match the expected format and convert compliance values to scores
  const transformedData = chartData.map((item) => {
    // Dynamically find the compliance question key
    const complianceKey = Object.keys(item).find(
      (k) => k.toLowerCase().includes("following all the treatment"),
    );
    const complianceValue = complianceKey
      ? (item[complianceKey] as ComplianceValue)
      : undefined;
    return {
      ...item,
      date: new Date(item.date).getTime(), // Convert to timestamp
      "Compliance Score": complianceValue
        ? complianceScoreMap[complianceValue]?.score ?? 0
        : 0,
      complianceText: complianceValue || "Unknown",
      timing_label: item.timing_label ?? item.timing ?? "No timing",
    };
  });

  return (
    <ChartContainer config={chartConfig}>
      <AreaChart
        accessibilityLayer
        data={transformedData}
        margin={{ top: 20, right: 0, left: 0, bottom: 0 }}
      >
        <XAxis
          dataKey="date"
          tickLine={false}
          tickMargin={10}
          axisLine={false}
          tickFormatter={(value) => {
            return dayjs(value).format("DD MMM");
          }}
        />
        <YAxis
          tickLine={false}
          tickMargin={10}
          axisLine={false}
          tickFormatter={(value) => {
            const complianceText = Object.entries(complianceScoreMap).find(
              ([_, data]) => data.score === value,
            )?.[0];
            return complianceText || "";
          }}
          ticks={[0, 1, 2, 3]}
          width={100}
        />
        <Area
          type="natural"
          dataKey="Compliance Score"
          stroke="hsl(var(--chart-2))"
          fill="hsl(var(--chart-2))"
          strokeWidth={2}
          isAnimationActive={!disableAnimations}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              hideLabel
              className="p-3 w-[200px]"
              formatter={(value, name, item) => {
                const complianceValue = item.payload
                  .complianceText as ComplianceValue;
                const complianceData = complianceScoreMap[complianceValue];

                return (
                  <>
                    <div className="mb-2 pb-2 border-b">
                      <div className="font-medium text-slate-900 text-sm">
                        {item.payload.timing}
                      </div>
                      <div className="mt-1 text-slate-600 text-xs">
                        {dayjs(item.payload.date).format("DD MMM YYYY")}
                      </div>
                    </div>

                    <p className="text-slate-800 text-sm">
                      <span
                        className={`px-1 py-0.5 rounded-sm w-fit tabular-nums ${
                          complianceData?.bgColor || "bg-gray-200"
                        } ${complianceData?.textColor || "text-gray-600"}`}
                      >
                        {complianceValue}
                      </span>{" "}
                      following advice
                    </p>
                  </>
                );
              }}
            />
          }
          cursor={false}
        />
      </AreaChart>
    </ChartContainer>
  );
}
